import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthContext } from './AuthContext';
import { getMyTenants, getTenants } from '../services/tenantService';
import { TENANT_STORAGE_KEY } from '../constants/tenancy';

const defaultValue = {
  tenant: '',
  setTenant: () => {},
  tenants: [],
  reloadTenants: () => {},
  tenantsLoading: false,
  tenantsError: '',
};

const TenantContext = createContext(defaultValue);

const readStoredTenant = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return localStorage.getItem(TENANT_STORAGE_KEY) ?? '';
};

export const TenantProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [tenant, setTenantState] = useState(readStoredTenant);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setTenant = useCallback((value) => {
    setTenantState(value);
  }, []);

  const syncTenantWithStorage = useCallback(
    (value) => {
      if (typeof window === 'undefined') {
        return;
      }

      if (value) {
        localStorage.setItem(TENANT_STORAGE_KEY, value);
      } else {
        localStorage.removeItem(TENANT_STORAGE_KEY);
      }
    },
    []
  );

  useEffect(() => {
    syncTenantWithStorage(tenant);
  }, [tenant, syncTenantWithStorage]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);

    try {
      let response;

      if (token) {
        try {
          response = await getMyTenants();
        } catch {
          // Um token expirado não deve esconder a lista pública da tela de login.
          response = await getTenants();
        }
      } else {
        response = await getTenants();
      }

      setTenants(Array.isArray(response) ? response : []);
      setError('');
    } catch (err) {
      setTenants([]);
      setError('Não foi possível carregar os clientes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [tenant, token]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!tenant && tenants.length === 1) {
      setTenantState(tenants[0].id);
      return;
    }

    if (!tenant) {
      return;
    }

    if (tenants.length > 0 && !tenants.some((item) => item.id === tenant)) {
      setTenantState('');
    }
  }, [tenant, tenants, loading]);

  const value = useMemo(
    () => ({
      tenant,
      setTenant,
      tenants,
      reloadTenants: fetchTenants,
      tenantsLoading: loading,
      tenantsError: error,
    }),
    [tenant, setTenant, tenants, fetchTenants, loading, error]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = () => useContext(TenantContext);

export default TenantContext;
